import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';

export interface AiSessionUnreadEntry {
  scenario: string;
  sessionId: string;
  finishedAt: number;
}

const UNREAD_STORAGE_PREFIX = 'aipd_ai_unread_sessions_';

function unreadStorageKey(): string {
  return `${UNREAD_STORAGE_PREFIX}${getUserSessionScopeSuffix()}`;
}

function loadUnread(): AiSessionUnreadEntry[] {
  try {
    const raw = localStorage.getItem(unreadStorageKey());
    if (!raw) return [];
    const arr = JSON.parse(raw) as AiSessionUnreadEntry[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveUnread(entries: AiSessionUnreadEntry[]) {
  try {
    localStorage.setItem(unreadStorageKey(), JSON.stringify(entries));
  } catch {
    /* ignore */
  }
}

function entryKey(scenario: string, sessionId: string): string {
  return `${scenario}::${sessionId}`;
}

export const useAiSessionNotifyStore = defineStore('aiSessionNotify', () => {
  const unreadEntries = ref<AiSessionUnreadEntry[]>(loadUnread());

  function persist() {
    saveUnread(unreadEntries.value);
  }

  function markUnread(scenario: string, sessionId: string) {
    const sc = scenario?.trim();
    const sid = sessionId?.trim();
    if (!sc || !sid) return;
    const key = entryKey(sc, sid);
    if (unreadEntries.value.some((e) => entryKey(e.scenario, e.sessionId) === key)) return;
    unreadEntries.value = [
      ...unreadEntries.value,
      { scenario: sc, sessionId: sid, finishedAt: Date.now() },
    ];
    persist();
  }

  function markRead(scenario: string, sessionId: string) {
    const sc = scenario?.trim();
    const sid = sessionId?.trim();
    if (!sc || !sid) return;
    const key = entryKey(sc, sid);
    const next = unreadEntries.value.filter((e) => entryKey(e.scenario, e.sessionId) !== key);
    if (next.length === unreadEntries.value.length) return;
    unreadEntries.value = next;
    persist();
  }

  function isUnread(scenario: string, sessionId: string): boolean {
    const key = entryKey(scenario, sessionId);
    return unreadEntries.value.some((e) => entryKey(e.scenario, e.sessionId) === key);
  }

  function hasUnreadInScenario(scenario: string): boolean {
    const sc = scenario?.trim();
    if (!sc) return false;
    return unreadEntries.value.some((e) => e.scenario === sc);
  }

  const unreadCountByScenario = computed(() => {
    const map: Record<string, number> = {};
    for (const e of unreadEntries.value) {
      map[e.scenario] = (map[e.scenario] ?? 0) + 1;
    }
    return map;
  });

  function resetForUserSwitch() {
    unreadEntries.value = [];
    try {
      localStorage.removeItem(unreadStorageKey());
    } catch {
      /* ignore */
    }
  }

  return {
    unreadEntries,
    markUnread,
    markRead,
    isUnread,
    hasUnreadInScenario,
    unreadCountByScenario,
    resetForUserSwitch,
  };
});
